import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ShoppingCart, ImageIcon, ChevronLeft, ChevronRight, X,
  Search, LogIn, Tag, Package, ArrowLeft, BookOpen
} from "lucide-react";
import axios from "axios";
import PublicHeader from "../components/PublicHeader";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const etatConfig = {
  neuf: { label: "Neuf", color: "bg-blue-100 text-blue-800" },
  occasion: { label: "Occasion", color: "bg-amber-100 text-amber-800" },
  reconditionne: { label: "Reconditionné", color: "bg-green-100 text-green-800" },
  comme_neuf: { label: "Comme neuf", color: "bg-teal-100 text-teal-800" },
};

const categoryLabels = {
  chargeur: "Chargeur", cable: "Câble", ecouteurs: "Écouteurs", coque: "Coque",
  vitre_protection: "Vitre de protection", souris: "Souris", clavier: "Clavier",
  cartouche: "Cartouche", cle_usb: "Clé USB", carte_memoire: "Carte mémoire",
  batterie: "Batterie", adaptateur: "Adaptateur", ecran: "Écran",
  disque_dur: "Disque dur", memoire_ram: "Mémoire RAM", ordinateur: "Ordinateur",
  smartphone: "Smartphone", tablette: "Tablette", imprimante: "Imprimante",
  livre_scolaire: "Livre scolaire", autre: "Autre"
};

const BoutiquePage = () => {
  const [neufs, setNeufs] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [livres, setLivres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/boutique/products`);
        setNeufs(res.data.neufs || []);
        setOccasions(res.data.occasions || []);
        setLivres(res.data.livres || []);
      } catch {
        // silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filterProducts = (products) => {
    if (!search) return products;
    const s = search.toLowerCase();
    return products.filter(p =>
      p.nom?.toLowerCase().includes(s) ||
      p.marque?.toLowerCase().includes(s) ||
      p.reference?.toLowerCase().includes(s) ||
      p.categorie?.toLowerCase().includes(s)
    );
  };

  const filteredNeufs = filterProducts(neufs);
  const filteredOccasions = filterProducts(occasions);
  const filteredLivres = filterProducts(livres);
  const showNeufs = activeTab === "all" || activeTab === "neufs";
  const showOccasions = activeTab === "all" || activeTab === "occasions";
  const showLivres = activeTab === "all" || activeTab === "livres";

  const openDetail = (product) => {
    setSelectedProduct(product);
    setCurrentPhotoIdx(0);
  };

  const closeDetail = () => {
    setSelectedProduct(null);
    setCurrentPhotoIdx(0);
  };

  const ProductCard = ({ product }) => {
    const etat = etatConfig[product.etat] || etatConfig.neuf;
    const photos = product.photos || [];
    return (
      <Card
        className="overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 group"
        data-testid={`boutique-product-${product.id}`}
        onClick={() => openDetail(product)}
      >
        <div className="relative h-44 bg-secondary/20 flex items-center justify-center overflow-hidden">
          {photos.length > 0 ? (
            <img src={`${BACKEND}${photos[0]}`} alt={product.nom} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <Package className="h-12 w-12 text-muted-foreground/20" />
          )}
          <Badge className={`absolute top-2 left-2 text-xs ${etat.color}`}>{etat.label}</Badge>
          {photos.length > 1 && (
            <div className="absolute bottom-1.5 right-1.5 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
              {photos.length} photos
            </div>
          )}
        </div>
        <CardContent className="p-3 space-y-1.5">
          <p className="font-semibold text-sm line-clamp-1">{product.nom}</p>
          {product.marque && <p className="text-xs text-muted-foreground">{product.marque}</p>}
          <div className="flex items-center justify-between pt-1">
            <span className="text-lg font-bold text-primary">{product.prix_vente?.toFixed(2)} EUR</span>
            <Link to="/login?redirect=/portal/boutique" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="outline" data-testid={`boutique-buy-${product.id}`}>
                <ShoppingCart className="h-3.5 w-3.5 mr-1" />
                Acheter
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicHeader />

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Location banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="boutique-location-banner">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-amber-600 shrink-0"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
          <p className="text-sm font-medium text-amber-900">À récupérer à la boutique de Bergevin dans le Super U, 97110 POINTE-À-PITRE</p>
          <p className="text-xs text-amber-800 mt-1">Lun, Mar, Jeu, Ven : 8h - 14h | Sam : 9h - 13h | Fermé Mer et Dim</p>
        </div>

        {/* Search & Tabs */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="boutique-search"
            />
          </div>
          <div className="flex gap-1 bg-muted p-1 rounded-lg overflow-x-auto">
            {[
              { key: "all", label: "Tous", count: neufs.length + occasions.length + livres.length },
              { key: "neufs", label: "Neufs", count: neufs.length },
              { key: "occasions", label: "Bonnes affaires", count: occasions.length },
              { key: "livres", label: "Livres scolaires", count: livres.length },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  activeTab === tab.key ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {/* Occasion Products Section */}
        {showOccasions && filteredOccasions.length > 0 && (
          <section data-testid="section-occasions">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="h-5 w-5 text-amber-600" />
              <h2 className="text-xl font-bold">Bonnes affaires</h2>
              <Badge variant="secondary">{filteredOccasions.length}</Badge>
            </div>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {filteredOccasions.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}

        {/* New Products Section */}
        {showNeufs && filteredNeufs.length > 0 && (
          <section data-testid="section-neufs">
            <div className="flex items-center gap-2 mb-4">
              <Package className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-bold">Produits neufs</h2>
              <Badge variant="secondary">{filteredNeufs.length}</Badge>
            </div>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {filteredNeufs.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}

        {/* Livres scolaires Section */}
        {showLivres && filteredLivres.length > 0 && (
          <section data-testid="section-livres">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-green-600" />
              <h2 className="text-xl font-bold">Livres scolaires</h2>
              <Badge variant="secondary">{filteredLivres.length}</Badge>
            </div>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {filteredLivres.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}

        {/* Empty state */}
        {filteredNeufs.length === 0 && filteredOccasions.length === 0 && filteredLivres.length === 0 && (
          <div className="text-center py-20">
            <Package className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="font-medium text-lg">Aucun produit trouvé</p>
            <p className="text-sm text-muted-foreground">Essayez une autre recherche</p>
          </div>
        )}

        {/* Info banner */}
        <div className="bg-primary/5 border border-primary/10 rounded-lg p-4 text-center" data-testid="boutique-info-banner">
          <p className="text-sm text-muted-foreground">
            Pour acheter un produit, <Link to="/login?redirect=/portal/boutique" className="text-primary font-medium hover:underline">connectez-vous ou créez un compte</Link>.
          </p>
        </div>
      </main>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-testid="boutique-product-modal"
          onClick={closeDetail}
        >
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64 sm:h-80 bg-secondary/30 flex items-center justify-center">
              {(selectedProduct.photos || []).length > 0 ? (
                <>
                  <img
                    src={`${BACKEND}${selectedProduct.photos[currentPhotoIdx]}`}
                    alt={selectedProduct.nom}
                    className="w-full h-full object-contain"
                  />
                  {selectedProduct.photos.length > 1 && (
                    <>
                      <button onClick={() => setCurrentPhotoIdx(i => (i - 1 + selectedProduct.photos.length) % selectedProduct.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={() => setCurrentPhotoIdx(i => (i + 1) % selectedProduct.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {selectedProduct.photos.map((_, idx) => (
                          <button key={idx} onClick={() => setCurrentPhotoIdx(idx)} className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentPhotoIdx ? "bg-white" : "bg-white/50"}`} />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <Package className="h-20 w-20 text-muted-foreground/30" />
              )}
              <button onClick={closeDetail} className="absolute top-3 right-3 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/70" data-testid="close-modal">
                <X className="h-4 w-4" />
              </button>
              <Badge className={`absolute top-3 left-3 ${(etatConfig[selectedProduct.etat] || etatConfig.neuf).color}`}>
                {(etatConfig[selectedProduct.etat] || etatConfig.neuf).label}
              </Badge>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <h2 className="text-xl font-bold">{selectedProduct.nom}</h2>
                {selectedProduct.marque && <p className="text-sm text-muted-foreground">{selectedProduct.marque}</p>}
                {selectedProduct.categorie && <Badge variant="outline" className="mt-1">{categoryLabels[selectedProduct.categorie] || selectedProduct.categorie}</Badge>}
              </div>
              {selectedProduct.description && (
                <p className="text-sm text-muted-foreground">{selectedProduct.description}</p>
              )}
              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <p className="text-3xl font-bold text-primary">{selectedProduct.prix_vente?.toFixed(2)} EUR</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedProduct.quantite_stock > 1
                      ? `${selectedProduct.quantite_stock} en stock`
                      : "Dernier exemplaire !"}
                  </p>
                </div>
                <Link to="/login?redirect=/portal/boutique">
                  <Button size="lg" data-testid="modal-buy-btn">
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Acheter
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoutiquePage;
